import { useCallback, useEffect, useRef, useState } from 'react';
import { getBookPath } from '../api/bookPath';

/** Scene Writing and the grid share this so a draft or an accept is reread. */
export const BOOK_PATH_CHANGED = 'brainstormy:book-path-changed';

/** Outline cards, the Plot Grid map, and the Framework header reread after an upload. */
export const OUTLINE_CHANGED = 'brainstormy:outline-changed';

export function noteBookPathChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(BOOK_PATH_CHANGED));
}

export function noteOutlineChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OUTLINE_CHANGED));
  noteBookPathChanged();
}

/**
 * This book's suggested-path state (free). refreshKey: bump it after an
 * Approve or Accept so the next step moves on. A failed read shows nothing.
 * status is 'loading' until the read for this story and refresh key lands,
 * including the render before the request starts.
 */
export default function useBookPath(storyId, refreshKey = 0) {
  const [state, setState] = useState({
    storyId: null,
    refreshKey: 0,
    path: null,
    status: 'idle',
  });

  const sameStory = state.storyId === storyId;
  const current = sameStory && state.refreshKey === refreshKey;
  const status = !storyId ? 'idle' : (current ? state.status : 'loading');
  const path = sameStory ? state.path : null;
  const requestGen = useRef(0);

  const refresh = useCallback(async () => {
    const gen = requestGen.current + 1;
    requestGen.current = gen;
    if (!storyId) {
      setState({ storyId: null, refreshKey, path: null, status: 'idle' });
      return null;
    }
    try {
      const next = await getBookPath(storyId);
      // A slower read from before this refresh must not put the old counts back.
      if (gen !== requestGen.current) return null;
      const landed = next || null;
      setState({ storyId, refreshKey, path: landed, status: 'ready' });
      return landed;
    } catch {
      if (gen !== requestGen.current) return null;
      setState({ storyId, refreshKey, path: null, status: 'ready' });
      return null;
    }
  }, [storyId, refreshKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!storyId || typeof window === 'undefined') return undefined;
    const onChange = () => { refresh(); };
    window.addEventListener(BOOK_PATH_CHANGED, onChange);
    return () => window.removeEventListener(BOOK_PATH_CHANGED, onChange);
  }, [refresh, storyId]);

  return { path, status, refresh };
}
